<template>
  <div
    :class="[
      'frame-full-video',
      { 'frame-full-video--featured': featured, 'frame-full-video--slider': isSlider },
    ]"
  >
    <div class="frame-full-video__slider" v-if="isSlider">
      <arrow-slider class="frame-full-video__slider__arrow-slider" @click="nextSlider" />
      <slick ref="slick" :options="slickOptions">
        <div
          class="frame-full-video__item"
          v-for="(video, index) in videos"
          :key="`${video}-${index}`"
        >
          <youtube-video
            :ref="`video${index}`"
            :ytid="video.id || video"
            :playerVars="playerVars"
            @playing="videoPlaying = true"
            @paused="videoPlaying = false"
          />

          <close-svg
            class="frame-full-video__close"
            @click="pauseVideo($event, `video${index}`)"
            v-show="videoPlaying"
          ></close-svg>

          <transition
            enter-active-class="animated fadeIn"
            leave-active-class="animated fadeOut"
            mode="out-in"
          >
            <div v-show="!videoPlaying">
              <div
                class="frame-full-video__placeholder"
                :style="{
                  background: `url(http://img.youtube.com/vi/${
                    video.id || video
                  }/maxresdefault.jpg) no-repeat center center`,
                }"
              ></div>

              <play-icon
                class="frame-full-video__play"
                width="100%"
                @click="playVideo($event, `video${index}`)"
              />
            </div>
          </transition>
        </div>
      </slick>
    </div>

    <div class="frame-full-video__default" v-if="!isSlider">
      <youtube-video
        ref="video"
        :ytid="videoId"
        :playerVars="playerVars"
        @playing="videoPlaying = true"
      />
      <close-svg
        class="frame-full-video__close"
        @click="pauseVideo()"
        v-show="videoPlaying"
      ></close-svg>
      <transition
        enter-active-class="animated fadeIn"
        leave-active-class="animated fadeOut"
        mode="out-in"
      >
        <div v-show="!videoPlaying">
          <div
            class="frame-full-video__placeholder"
            :style="{
              background: `url(http://img.youtube.com/vi/${videoId}/maxresdefault.jpg) no-repeat center center`,
            }"
          ></div>

          <play-icon class="frame-full-video__play" width="100%" @click="playVideo" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Slick from "vue-slick";
import { Player } from "vue-youtube-iframe-api";
import ArrowSlider from "@/assets/arrow-slider.svg";
import PlaySVG from "@/assets/play.svg";
import CloseSVG from "@/assets/close-white.svg";

export default {
  name: "frame-full-video",

  components: {
    "play-icon": PlaySVG,
    "youtube-video": Player,
    Slick,
    "arrow-slider": ArrowSlider,
    "close-svg": CloseSVG,
  },

  props: {
    videoId: {
      type: [String, null],
      default: null,
    },

    videos: {
      type: Array,
    },

    isSlider: {
      type: Boolean,
      default: false,
    },

    featured: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      videoPlaying: false,
      currentVideo: 0,
      slickOptions: {
        slidesToShow: 1,
        variableWidth: true,
        arrows: false,
        infinite: true,
        speed: 900,
        cssEase: "cubic-bezier(.15,.22,.6,.9)",
      },
      playerVars: {
        height: "100%",
        width: "100%",
        modestbranding: 1,
        showinfo: 1,
        enablejsapi: 1,
        rel: 0,
      },
    };
  },
  methods: {
    nextSlider() {
      if (this.videoPlaying) {
        const iframe = this.$refs[this.currentVideo][0];
        iframe.player.pauseVideo();
        this.videoPlaying = false;
      }

      this.$refs.slick.next();
    },

    playVideo($event, reference) {
      this.$nextTick(() => {
        const iframe = reference ? this.$refs[reference][0] : this.$refs.video;

        this.currentVideo = reference;

        iframe.player.playVideo();
        this.videoPlaying = true;
      });
    },

    pauseVideo($event, reference) {
      this.$nextTick(() => {
        const iframe = reference ? this.$refs[reference][0] : this.$refs.video;
        iframe.player.pauseVideo();
        this.videoPlaying = false;
      });
    },
  },
};
</script>

<style lang="scss">
.frame-full-video {
  height: auto;
  width: 100%;
  position: relative;
  padding: 30px;

  &__default {
    position: relative;
  }

  &--featured {
    padding: 0 30px 0 0;

    .frame-full-video {
      &__placeholder {
        margin: 0;
        width: 100%;
        left: auto;
        height: 100%;
      }
    }
  }

  &--slider {
    padding: 0;

    .frame-full-video {
      &__placeholder {
        margin: 0;
        height: 100%;
      }
    }
  }

  iframe {
    width: 100%;
    height: 50vw;
    display: block;
    position: relative;
    max-height: 900px;
  }

  &__placeholder {
    height: 100%;
    position: absolute;
    top: 0;
    width: auto;
    left: 0;
    right: 0;
    background-size: cover !important;
  }

  &__play {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    width: 60px;
    height: 60px;
    transform: scale(1);
    transition: all 0.3s ease-out;
    z-index: 5;
    cursor: pointer;

    image {
      width: 60px;
      height: 60px;
    }

    &:hover {
      transform: scale(1.2);
      transition: all 0.3s ease-out;
    }
  }

  &__close {
    position: absolute;
    right: 30px;
    top: 15px;
    transform: scale(1);
    transition: all 0.3s ease-out;
    background: black;
    padding: 10px 0px 2px 10px;
    box-sizing: content-box;

    svg,
    image {
      width: 30px;
      height: 30px;
      cursor: pointer;
    }

    &:hover {
      transform: scale(1.2);
      transition: all 0.3s ease-out;
    }
  }

  &__slider {
    .slick-track {
      display: flex;
      overflow: hidden;
      width: 100%;
    }

    .slick-slider {
      overflow: hidden;
      width: 90%;
      left: 10%;
      position: relative;
    }

    .slick-list {
      position: relative;
      overflow: hidden;
      width: 100%;
    }

    .slick-slide {
      width: 90vw;
      margin-left: 8px;
      overflow: hidden;
      -webkit-transform: translate3d(0, 0, 0);
      -webkit-backface-visibility: hidden;
      transition: opacity 0.3s ease-out;
    }

    .slick-slider .slick-track,
    .slick-slider .slick-list {
      -webkit-perspective: 1000px;
      perspective: 1000px;
    }

    &__arrow-slider {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 40px;
      margin: auto;
      width: 80px;
      height: 80px;
      cursor: pointer;
      transition: all 0.3s ease-out;
      opacity: 1;

      image {
        width: 80px;
        height: 80px;
      }

      &:hover {
        opacity: 0.5;
        transition: opacity 0.3s ease-in-out;
      }
    }
  }
}

@media (max-width: 1400px) {
  .frame-full-video {
    &__slider {
      &__arrow-slider {
        left: 30px;
        width: 60px;
        height: 60px;

        image {
          width: 60px;
          height: 60px;
        }
      }
    }
  }
}

@media (max-width: 1000px) {
  .frame-full-video {
    &__slider {
      &__arrow-slider {
        left: 20px;
        width: 40px;
        height: 40px;

        image {
          width: 40px;
          height: 40px;
        }
      }
    }

    &__item {
      height: 55vw;
    }

    &__play {
      width: 40px;
      height: 60px;

      image {
        width: 40px;
        height: 60px;
      }
    }
  }
}

@media (max-width: 700px) {
  .frame-full-video {
    &__slider {
      &__arrow-slider {
        left: 10px;
        width: 40px;
        height: 40px;

        image {
          width: 40px;
          height: 40px;
        }
      }
    }

    .slick-slider {
      width: 85%;
      left: 15%;
    }

    &__play {
      width: 30px;
      height: 50px;

      image {
        width: 30px;
        height: 50px;
      }
    }

    &--featured {
      padding: 0 30px 0 0;

      .frame-full-video {
        &__placeholder {
          margin: 0;
          width: 100%;
          left: auto;
        }
      }
    }
  }
}

@media (max-width: 400px) {
  .frame-full-video {
    &__slider {
      &__arrow-slider {
        left: 10px;
        width: 30px;
        height: 30px;

        image {
          width: 30px;
          height: 30px;
        }
      }
    }

    .slick-slider {
      width: 85%;
      left: 15%;
    }
  }
}
</style>
